import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import {
  Layers,
  FileText,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  Shield,
  Brain,
  FileQuestion,
  Sparkles,
  User,
  CreditCard,
  Link2,
  Copy,
  Eye,
  EyeOff,
  Flag,
  Calendar,
  MapPin,
} from 'lucide-react';

import ContradictionsAutoSurface from '@/pages/intelligence-studio/components/ContradictionsAutoSurface';
import intelligenceStudioApi from '../../../api/intelligenceStudioApi';
import { MarkdownText } from '@/components/RichChat/MarkdownText';
import { getFileIcon, getFileTypeTooltip } from '@/pages/intelligence-studio/utils/fileUtils';

const FILE_ANALYSIS_MAX_RETRIES = 3;
const FILE_ANALYSIS_BACKOFF_BASE_MS = 1000;

const isGhostFile = file => {
  const status = String(file?.status || '').toLowerCase();
  if (status === 'ghost') return true;
  const ghostStatus = String(file?.ghostStatus || file?.ghost_status || '').toUpperCase();
  if (ghostStatus && ghostStatus !== 'NONE' && ghostStatus !== 'RECOVERED') return true;
  if (file?._ghost === true || file?.isGhost === true) return true;
  return false;
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIDENCE-BASED STATUS (Replaces action buttons for MAXIMUM TRUST)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Determine the confidence level of file analysis
 * Returns: 'verified' | 'semantic' | 'partial' | 'pending'
 */
const getFileConfidenceLevel = (file, analysisResult) => {
  const analysis = analysisResult?.analysis;
  const trustState = getEffectiveTrustState(file);

  // Not ready yet
  if (trustState === 'processing' || trustState === 'restoring') {
    return 'pending';
  }

  // No analysis yet (but file may already be ready)
  if (!analysis) {
    return 'partial';
  }

  // Check if analysis is metadata-only
  const isMetadataOnly =
    analysis.textSource === 'metadata_fallback' || analysis.isMetadataOnly === true;
  if (isMetadataOnly) {
    return 'partial';
  }

  // Check deterministic coverage
  const deterministicCoverage =
    analysis.confidenceCoverage?.deterministicCoveragePercent ||
    analysis.deterministicValidation?.coveragePercent ||
    0;
  const semanticConfidence = analysis.confidenceCoverage?.semanticConfidence || 'medium';

  // High deterministic coverage + high semantic = verified
  if (deterministicCoverage >= 70 && semanticConfidence === 'high') {
    return 'verified';
  }

  // Has meaningful analysis content = semantic understanding
  if (analysis.executiveSummary?.headline && analysis.executiveSummary.headline.length > 10) {
    return 'semantic';
  }

  // Has some analysis but limited
  return 'partial';
};

/**
 * Get confidence badge props for display
 */
const getConfidenceBadge = level => {
  switch (level) {
    case 'verified':
      return {
        icon: Shield,
        label: 'Verified',
        tooltip: 'Content verified with deterministic checks',
        className:
          'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800/40',
      };
    case 'semantic':
      return {
        icon: Brain,
        label: 'Understood',
        tooltip: 'Semantically understood by AI',
        className:
          'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800/40',
      };
    case 'partial':
      return {
        icon: FileQuestion,
        label: 'Ready',
        tooltip: 'Document is ready; deeper analysis will continue in the background',
        className:
          'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800/40',
      };
    case 'pending':
    default:
      return {
        icon: Clock,
        label: 'Processing',
        tooltip: 'Analysis in progress',
        className:
          'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700',
      };
  }
};

/**
 * Determine notebook-level readiness
 */
const getNotebookReadiness = (files, analysisData) => {
  if (!files || files.length === 0) return { ready: false, reason: 'no_files' };
  const isSingle = files.length === 1;

  const fileStatuses = files.map(f => {
    const fileId = f.id || f.fileId;
    const analysisResult = analysisData?.fileAnalyses?.[fileId];
    return {
      file: f,
      trustState: getEffectiveTrustState(f),
      confidenceLevel: getFileConfidenceLevel(f, analysisResult),
      hasAnalysis: !!analysisResult?.analysis,
    };
  });

  const pendingCount = fileStatuses.filter(s =>
    ['processing', 'restoring'].includes(s.trustState)
  ).length;
  const lowQualityCount = fileStatuses.filter(s => s.trustState === 'low_quality').length;
  const verifiedCount = fileStatuses.filter(s => s.confidenceLevel === 'verified').length;
  const semanticCount = fileStatuses.filter(s => s.confidenceLevel === 'semantic').length;
  const partialCount = fileStatuses.filter(s => s.confidenceLevel === 'partial').length;
  const totalWithAnalysis = verifiedCount + semanticCount + partialCount;

  if (pendingCount > 0) {
    return {
      ready: false,
      reason: 'processing',
      message: isSingle
        ? 'Preparing document…'
        : `${pendingCount} document${pendingCount > 1 ? 's' : ''} still processing`,
    };
  }

  if (lowQualityCount > 0) {
    return {
      ready: false,
      reason: 'low_quality',
      message: isSingle
        ? 'Document needs a clearer scan'
        : `${lowQualityCount} document${lowQualityCount > 1 ? 's' : ''} need a clearer scan`,
    };
  }

  if (totalWithAnalysis === files.length) {
    if (verifiedCount === files.length) {
      return {
        ready: true,
        level: 'verified',
        message: isSingle ? 'Document verified' : 'All documents verified',
      };
    }
    if (verifiedCount + semanticCount === files.length) {
      return {
        ready: true,
        level: 'semantic',
        message: isSingle ? 'Document understood' : 'All documents understood',
      };
    }
    return {
      ready: true,
      level: 'partial',
      message: isSingle ? 'Document ready' : 'All documents ready',
    };
  }

  return { ready: false, reason: 'incomplete' };
};

const normalizeTrustState = state => {
  const s = String(state || '').toLowerCase();
  if (s === 'reading') return 'processing'; // backward compat
  // ✅ CACHE FIX: 'restoring' is a fast cache hit - treat similar to processing but UI can show different message
  if (s === 'restoring' || s === 'cache_restore') return 'restoring';
  if (s === 'trusted' || s === 'readable' || s === 'low_quality' || s === 'processing') return s;
  return s ? 'processing' : 'processing';
};

const getEffectiveTrustState = file => {
  const normalized = normalizeTrustState(file?.userTrust?.state);
  if (normalized !== 'processing') return normalized;

  const status = String(file?.lifecycleSnapshot?.status || file?.status || '').toLowerCase();
  if (status === 'ready' || status === 'ready_with_warnings') return 'readable';
  if (status === 'blocked_low_quality') return 'low_quality';
  if (status === 'failed' || status === 'error') return 'low_quality';
  return normalized;
};

const sanitizeMicroInsight = (text, { isReady }) => {
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (!isReady) return raw;
  return raw
    .replace(/requires\s+ocr\.?/gi, '')
    .replace(/needs\s+ocr\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/,\s*$/g, '')
    .trim();
};

const getFileName = f => f?.originalName || f?.name || f?.filename || 'Document';

const getFileExt = fileName => {
  const n = String(fileName || '');
  return n.includes('.') ? n.split('.').pop()?.toLowerCase() || '' : '';
};

const normalizeCertainty = c => {
  const s = String(c || '').toLowerCase();
  if (s === 'high' || s === 'medium' || s === 'low') return s;
  if (s === 'med') return 'medium';
  return 'medium';
};

// ═══════════════════════════════════════════════════════════════════════════════
// IDENTITY DOCUMENT DISPLAY (Universal - Premium Wallet Style)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Country code to flag emoji mapping
 */
const getCountryFlag = code => {
  if (!code || typeof code !== 'string') return null;
  const normalizedCode = code.toUpperCase().trim();

  // Common ISO Alpha-2 and Alpha-3 mappings
  const codeMap = {
    KWT: '🇰🇼',
    KW: '🇰🇼',
    ARE: '🇦🇪',
    AE: '🇦🇪',
    UAE: '🇦🇪',
    SAU: '🇸🇦',
    SA: '🇸🇦',
    USA: '🇺🇸',
    US: '🇺🇸',
    GBR: '🇬🇧',
    GB: '🇬🇧',
    UK: '🇬🇧',
    DEU: '🇩🇪',
    DE: '🇩🇪',
    FRA: '🇫🇷',
    FR: '🇫🇷',
    JPN: '🇯🇵',
    JP: '🇯🇵',
    CHN: '🇨🇳',
    CN: '🇨🇳',
    IND: '🇮🇳',
    IN: '🇮🇳',
    EGY: '🇪🇬',
    EG: '🇪🇬',
    JOR: '🇯🇴',
    JO: '🇯🇴',
    LBN: '🇱🇧',
    LB: '🇱🇧',
    SYR: '🇸🇾',
    SY: '🇸🇾',
    IRQ: '🇮🇶',
    IQ: '🇮🇶',
    BHR: '🇧🇭',
    BH: '🇧🇭',
    QAT: '🇶🇦',
    QA: '🇶🇦',
    OMN: '🇴🇲',
    OM: '🇴🇲',
    PAK: '🇵🇰',
    PK: '🇵🇰',
    BGD: '🇧🇩',
    BD: '🇧🇩',
    PHL: '🇵🇭',
    PH: '🇵🇭',
  };

  return codeMap[normalizedCode] || null;
};

/**
 * Get document type display info
 */
const getDocumentTypeInfo = type => {
  const typeMap = {
    PASSPORT: { label: 'Passport', icon: '🛂', color: 'blue' },
    NATIONAL_ID: { label: 'National ID', icon: '🪪', color: 'green' },
    DRIVER_LICENSE: { label: 'Driver License', icon: '🚗', color: 'purple' },
    RESIDENCE_PERMIT: { label: 'Residence Permit', icon: '🏠', color: 'teal' },
    WORK_PERMIT: { label: 'Work Permit', icon: '💼', color: 'amber' },
    OTHER_ID: { label: 'Identity Document', icon: '📄', color: 'slate' },
  };
  return typeMap[type] || typeMap['OTHER_ID'];
};

/**
 * Get document class display info (Universal Authority Documents)
 */
const getDocumentClassInfo = docClass => {
  const classMap = {
    IDENTITY: { label: 'Identity Document', color: 'blue', icon: 'id' },
    CERTIFICATE: { label: 'Certificate', color: 'purple', icon: 'certificate' },
    LICENSE: { label: 'License', color: 'green', icon: 'license' },
    REGISTRATION: { label: 'Registration', color: 'teal', icon: 'registration' },
    PERMIT: { label: 'Permit', color: 'amber', icon: 'permit' },
    COURT_DOC: { label: 'Court Document', color: 'red', icon: 'court' },
    GOVERNMENT_NOTICE: { label: 'Official Notice', color: 'indigo', icon: 'notice' },
    OTHER_AUTHORITY: { label: 'Official Document', color: 'slate', icon: 'doc' },
  };
  return classMap[docClass] || classMap['OTHER_AUTHORITY'];
};

/**
 * Render Authority Overview (Premium Wallet / Certificate Style)
 * Universal for all authoritative documents
 */
const AuthorityOverview = ({ authorityEntity, file, isLegacy = false }) => {
  const [showFullNumber, setShowFullNumber] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const copiedTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      try {
        if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      } catch (_) {}
    };
  }, []);

  if (!authorityEntity || !authorityEntity.primitives) {
    return null;
  }

  const primitives = authorityEntity.primitives;
  const confidence = authorityEntity.extraction_confidence || primitives.extraction_confidence || 0;
  const documentClass =
    authorityEntity.documentClass || primitives.document_class || 'OTHER_AUTHORITY';
  const entityType = authorityEntity.entityType || primitives.entity_type || 'UNKNOWN';
  const isVerified =
    confidence >= 0.85 &&
    (primitives.mrz_present ||
      primitives.validation_signals?.includes('DOC_NUM_FORMAT_VALID') ||
      primitives.validation_signals?.length > 2);
  const docClassInfo = getDocumentClassInfo(documentClass);
  const docTypeInfo = getDocumentTypeInfo(primitives.document_type);
  const flag = getCountryFlag(primitives.nationality) || getCountryFlag(primitives.issuer_country);
  const links = authorityEntity.links || [];

  const handleCopySafe = () => {
    // Copy only masked/safe values
    const safeData = {
      name: primitives.subject_name_primary || primitives.full_name || 'Unknown',
      documentType: documentClass,
      entityType: entityType,
      nationality: primitives.nationality,
      issuer: primitives.issuer_name,
      documentNumber: primitives.document_number || '****', // Already masked from backend
    };
    navigator.clipboard.writeText(JSON.stringify(safeData, null, 2));
    setCopied(true);
    try {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    } catch (_) {}
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Authority Document Card Header */}
      <div className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 overflow-hidden">
        {/* Top bar with document class */}
        <div
          className={`px-4 py-2 flex items-center justify-between ${
            docClassInfo.color === 'blue'
              ? 'bg-blue-600 text-white'
              : docClassInfo.color === 'green'
                ? 'bg-emerald-600 text-white'
                : docClassInfo.color === 'purple'
                  ? 'bg-purple-600 text-white'
                  : docClassInfo.color === 'teal'
                    ? 'bg-teal-600 text-white'
                    : docClassInfo.color === 'amber'
                      ? 'bg-amber-600 text-white'
                      : docClassInfo.color === 'red'
                        ? 'bg-red-600 text-white'
                        : docClassInfo.color === 'indigo'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-600 text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="text-sm font-semibold">{docClassInfo.label}</span>
            {docTypeInfo && docTypeInfo.label !== docClassInfo.label && (
              <span className="text-xs opacity-75">• {docTypeInfo.label}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isVerified ? (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-medium">
                <Shield className="h-3 w-3" />
                <span>VERIFIED FORMAT</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-medium">
                <CheckCircle className="h-3 w-3" />
                <span>RECOGNIZED</span>
              </div>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="p-4">
          <div className="flex gap-4">
            {/* Photo placeholder */}
            <div className="w-20 h-24 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 border border-slate-300 dark:border-slate-600">
              {primitives.photo_present ? (
                <span className="text-2xl">📷</span>
              ) : (
                <User className="h-8 w-8 text-slate-400 dark:text-slate-500" />
              )}
            </div>

            {/* Info grid */}
            <div className="flex-1 space-y-2">
              {/* Subject Name (Person or Organization) */}
              {(primitives.subject_name_primary || primitives.full_name) && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {entityType === 'PERSON'
                      ? 'Full Name'
                      : entityType === 'ORGANIZATION'
                        ? 'Organization'
                        : 'Subject'}
                  </div>
                  <div className="text-base font-bold text-slate-900 dark:text-white" dir="auto">
                    {primitives.subject_name_primary || primitives.full_name}
                  </div>
                  {(primitives.subject_name_secondary || primitives.name_local) &&
                    (primitives.subject_name_secondary || primitives.name_local) !==
                      (primitives.subject_name_primary || primitives.full_name) && (
                      <div className="text-sm text-slate-600 dark:text-slate-300" dir="auto">
                        {primitives.subject_name_secondary || primitives.name_local}
                      </div>
                    )}
                </div>
              )}

              {/* Document Number */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Document No.
                  </div>
                  <div className="text-sm font-mono font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <span>{primitives.document_number || '****'}</span>
                    <button
                      onClick={() => setShowFullNumber(!showFullNumber)}
                      className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400"
                      title={showFullNumber ? 'Hide' : 'Cannot reveal - masked for safety'}
                    >
                      <EyeOff className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                {flag && (
                  <div
                    className="text-2xl"
                    title={primitives.nationality || primitives.issuer_country}
                  >
                    {flag}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details grid */}
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            {primitives.date_of_birth && (
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Date of Birth
                  </div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    {primitives.date_of_birth}
                  </div>
                </div>
              </div>
            )}

            {primitives.sex && primitives.sex !== 'Unknown' && (
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Sex
                  </div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    {primitives.sex === 'M'
                      ? 'Male'
                      : primitives.sex === 'F'
                        ? 'Female'
                        : primitives.sex}
                  </div>
                </div>
              </div>
            )}

            {primitives.nationality && (
              <div className="flex items-start gap-2">
                <Flag className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Nationality
                  </div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    {primitives.nationality}
                  </div>
                </div>
              </div>
            )}

            {primitives.expiry_date && (
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Expiry
                  </div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    {primitives.expiry_date}
                  </div>
                </div>
              </div>
            )}

            {primitives.issuer_authority && (
              <div className="flex items-start gap-2 col-span-2">
                <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Issuer
                  </div>
                  <div className="font-medium text-slate-900 dark:text-white">
                    {primitives.issuer_authority}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Validation signals */}
          {primitives.validation_signals && primitives.validation_signals.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {primitives.validation_signals.map((signal, idx) => (
                <span
                  key={idx}
                  className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                    signal.includes('VALID') || signal.includes('DETECTED')
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : signal.includes('SUSPICIOUS') || signal.includes('IMPLAUSIBLE')
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  }`}
                >
                  {signal.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}

          {/* Links to other documents */}
          {links.length > 0 && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <Link2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-800 dark:text-blue-200">
                Linked to {links.length} other document{links.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-2 bg-slate-100 dark:bg-slate-800/50 flex items-center justify-between border-t border-slate-200 dark:border-slate-700">
          <div className="text-[10px] text-slate-500 dark:text-slate-400">
            Confidence: {(confidence * 100).toFixed(0)}%
            {primitives.mrz_present && ' • MRZ detected'}
          </div>
          <button
            onClick={handleCopySafe}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          >
            <Copy className="h-3 w-3" />
            {copied ? 'Copied!' : 'Copy Safe'}
          </button>
        </div>
      </div>

      {/* Confident authority statement */}
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Shield className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
              We know exactly what this is.
            </div>
            <div className="text-xs text-emerald-700 dark:text-emerald-300">
              {documentClass === 'IDENTITY'
                ? "This is an official identity document. All recognized fields have been extracted and secured above. You're in control."
                : `This is an official ${docClassInfo.label.toLowerCase()}. We extracted the structured data that matters. You\'re in control.`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Legacy alias for backward compatibility
const IdentityOverview = AuthorityOverview;

/**
 * Render Nested Authority Entities (for documents containing embedded official docs)
 */
const NestedAuthorityEntities = ({ authorityEntity, isLegacy = false }) => {
  // Handle both old IDENTITY_NESTED and new AUTHORITY_NESTED
  if (
    !authorityEntity ||
    (authorityEntity.mode !== 'AUTHORITY_NESTED' && authorityEntity.mode !== 'IDENTITY_NESTED')
  ) {
    return null;
  }

  const primitives = authorityEntity.primitives;
  const documentClass =
    authorityEntity.documentClass || primitives?.document_class || 'OTHER_AUTHORITY';
  const docClassInfo = getDocumentClassInfo(documentClass);
  const docTypeInfo = getDocumentTypeInfo(primitives?.document_type);
  const flag = getCountryFlag(primitives?.nationality);

  return (
    <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="h-4 w-4 text-purple-600 dark:text-purple-400" />
        <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
          Embedded {docClassInfo.label} Detected
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-purple-200 dark:border-purple-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{docTypeInfo?.icon || '📄'}</span>
            <div>
              <div className="text-sm font-medium text-slate-900 dark:text-white">
                {primitives?.subject_name_primary || primitives?.full_name || 'Name not extracted'}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {docClassInfo.label} • {primitives?.document_number || '****'}
              </div>
            </div>
          </div>
          {flag && <span className="text-xl">{flag}</span>}
        </div>

        {primitives?.validation_signals?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {primitives.validation_signals.slice(0, 3).map((signal, idx) => (
              <span
                key={idx}
                className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
              >
                {signal.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 text-xs text-purple-700 dark:text-purple-300">
        This document contains an embedded {docClassInfo.label.toLowerCase()}. Full document
        analysis continues below.
      </div>
    </div>
  );
};

// Legacy alias for backward compatibility
const NestedIdentityEntities = NestedAuthorityEntities;

const scoreFinding = finding => {
  if (!finding) return 0;
  const text = String(finding?.text || '').trim();
  if (!text) return 0;

  const cert = normalizeCertainty(finding?.certainty);
  const hasQuote = Array.isArray(finding?.quotes)
    ? finding.quotes.some(q => String(q || '').trim().length >= 40)
    : false;
  const hasAnchors = Array.isArray(finding?.citationAnchors)
    ? finding.citationAnchors.length > 0
    : false;

  let score = cert === 'high' ? 65 : cert === 'medium' ? 50 : 35;
  if (hasQuote) score += 15;
  if (hasAnchors) score += 10;
  if (text.length >= 120) score += 5;
  return Math.min(100, score);
};

const pickBestFinding = file => {
  const findings = Array.isArray(file?.autoBriefFindings) ? file.autoBriefFindings : [];
  if (findings.length === 0) return null;
  let best = null;
  let bestScore = 0;
  for (const f of findings) {
    const s = scoreFinding(f);
    if (s > bestScore) {
      bestScore = s;
      best = f;
    }
  }
  if (!best) return null;
  return { ...best, _score: bestScore };
};

const TabIcon = ({ fileName, className = 'h-4 w-4' }) => {
  const icon = getFileIcon(fileName || '');
  const ext =
    String(fileName || '')
      .split('.')
      .pop()
      ?.toLowerCase() || '';
  const tooltip = getFileTypeTooltip(ext);

  if (typeof icon === 'string' && icon.startsWith('http')) {
    return (
      <img
        src={icon}
        alt={ext || 'file'}
        title={tooltip}
        className={className}
        style={{ objectFit: 'contain' }}
      />
    );
  }
  return (
    <span className="text-base" title={tooltip}>
      {icon}
    </span>
  );
};

const TrustPill = ({ trust }) => {
  if (!trust) return null;
  const state = String(trust.state || '').toLowerCase();
  const cls =
    state === 'trusted'
      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      : state === 'readable'
        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
        : state === 'low_quality'
          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          : state === 'processing' || state === 'reading'
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
            : 'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200';
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${cls}`}
      title={trust.tooltip}
    >
      <span>{trust.emoji}</span>
      <span>{trust.badge}</span>
    </span>
  );
};

/**
 * NotebookInsightsTabs
 * - Renders the initial notebook summary directly under the Nano-Metadata table
 * - Provides a horizontal tab bar: All Documents + one tab per file
 * - Uses the same file-type icons contract (VS Code Icons CDN via getFileIcon)
 */
export default function NotebookInsightsTabs({
  notebook,
  summaryText,
  summaryTitle = 'Unified Summary',
  anchorId = 'is-summary',
  onQuickAsk,
}) {
  const files = Array.isArray(notebook?.files) ? notebook.files : [];
  const isSingleFileMode = files.length <= 1;
  const baseNotebookTrustState = normalizeTrustState(notebook?.userTrustSummary?.state);
  const notebookTrustState = useMemo(() => {
    if (baseNotebookTrustState !== 'processing') return baseNotebookTrustState;
    if (!files.length) return baseNotebookTrustState;
    const states = files.map(getEffectiveTrustState);
    if (states.some(s => s === 'low_quality')) return 'low_quality';
    if (states.some(s => s === 'processing' || s === 'restoring')) return 'processing';
    return 'readable';
  }, [baseNotebookTrustState, files]);
  const vaultNarrativeText =
    typeof notebook?.vaultNarrative === 'string'
      ? notebook.vaultNarrative.trim()
      : typeof notebook?.vaultNarrative?.text === 'string'
        ? notebook.vaultNarrative.text.trim()
        : '';
  const headerTitle = isSingleFileMode ? 'Document Summary' : 'Notebook Summary';
  const headerSubtitle = isSingleFileMode
    ? 'Summary and key points for this document'
    : 'One integrated view • All documents + per-file briefs';
  const resolvedSummaryTitle =
    isSingleFileMode && summaryTitle === 'Unified Summary' ? 'Document Summary' : summaryTitle;

  // ✅ Insight Memory: persisted continuity of proactive insights (what/why/actions/user did)
  const [insightMemory, setInsightMemory] = useState([]);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memoryLoading, setMemoryLoading] = useState(false);

  // ✅ Prevent async work from updating state/logging after unmount (Jest + real UX)
  const cancelRef = useRef(false);
  const insightAbortRef = useRef(null);
  const analysisAbortRef = useRef(null);
  const runAnalysisAbortRef = useRef(null);

  const isCanceledRequest = e =>
    e?.name === 'CanceledError' ||
    e?.code === 'ERR_CANCELED' ||
    String(e?.message || '').toLowerCase().includes('canceled');

  useEffect(() => {
    cancelRef.current = false;
    return () => {
      cancelRef.current = true;
      try {
        insightAbortRef.current?.abort?.();
      } catch (_) {}
      try {
        analysisAbortRef.current?.abort?.();
      } catch (_) {}
      try {
        runAnalysisAbortRef.current?.abort?.();
      } catch (_) {}
    };
  }, [notebook?.id]);

  const normalizeMemoryResponse = res => {
    const outer = res?.data?.data || res?.data || res;
    // API returns { success, data } from our controller; apiClient may already unwrap.
    if (
      outer &&
      typeof outer === 'object' &&
      Object.prototype.hasOwnProperty.call(outer, 'success')
    ) {
      return Array.isArray(outer.data) ? outer.data : [];
    }
    return Array.isArray(outer) ? outer : [];
  };

  const loadInsightMemory = useCallback(async () => {
    if (!notebook?.id) return;
    try {
      try {
        insightAbortRef.current?.abort?.();
      } catch (_) {}
      const controller = new AbortController();
      insightAbortRef.current = controller;

      if (!cancelRef.current) setMemoryLoading(true);
      const res = await intelligenceStudioApi.getInsightMemory(notebook.id, { signal: controller.signal });
      const mem = normalizeMemoryResponse(res);
      if (cancelRef.current) return;
      setInsightMemory(mem);
    } catch (e) {
      if (cancelRef.current || isCanceledRequest(e)) return;
      // Non-fatal: memory is additive. If unavailable, we still show live pounces.
      console.warn('[InsightMemory] Failed to load:', e?.message || e);
    } finally {
      if (cancelRef.current) return;
      setMemoryLoading(false);
    }
  }, [notebook?.id]);

  useEffect(() => {
    loadInsightMemory();
  }, [loadInsightMemory]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ ENTERPRISE: Post-Ingestion Analysis Engine Integration
  // Fetch analysis results from the unified analysis engine
  // ═══════════════════════════════════════════════════════════════════════════
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [fileAnalysisFailures, setFileAnalysisFailures] = useState({});
  const fileAnalysisFailuresRef = useRef({});
  const blockedFileAnalysisRef = useRef(new Set());
  const retryTimersRef = useRef(new Map());
  const [analysisRetryEpoch, setAnalysisRetryEpoch] = useState(0);

  useEffect(() => {
    fileAnalysisFailuresRef.current = fileAnalysisFailures;
  }, [fileAnalysisFailures]);

  const scheduleAnalysisRetry = useCallback((fileId, delayMs) => {
    try {
      const timers = retryTimersRef.current;
      if (timers.has(fileId)) {
        clearTimeout(timers.get(fileId));
      }
      const timeoutId = setTimeout(() => {
        timers.delete(fileId);
        setAnalysisRetryEpoch(Date.now());
      }, Math.max(0, delayMs || 0));
      timers.set(fileId, timeoutId);
    } catch (_) {}
  }, []);

  const clearFileAnalysisFailure = useCallback(fileId => {
    if (!fileId) return;
    setFileAnalysisFailures(prev => {
      if (!prev[fileId]) return prev;
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    try {
      blockedFileAnalysisRef.current.delete(String(fileId));
    } catch (_) {}
    try {
      const timers = retryTimersRef.current;
      if (timers.has(fileId)) {
        clearTimeout(timers.get(fileId));
        timers.delete(fileId);
      }
    } catch (_) {}
  }, []);

  const recordFileAnalysisFailure = useCallback((fileId, error) => {
    if (!fileId) return;
    const errorData = error?.response?.data || error?.data || {};
    const status = error?.response?.status;
    const errorCode =
      errorData?.code || errorData?.error || errorData?.reason || errorData?.status || null;
    const rawMessage =
      errorData?.message || errorData?.error || errorData?.details?.error || error?.message || '';
    const message = String(rawMessage || 'File analysis failed').trim();
    const canRetryFlag =
      typeof errorData?.canRetry === 'boolean'
        ? errorData.canRetry
        : typeof errorData?.retryable === 'boolean'
          ? errorData.retryable
          : null;
    const lowerMessage = message.toLowerCase();
    const notFoundHint =
      lowerMessage.includes('file not found') || lowerMessage.includes('not found in notebook');
    const isClientError = status === 400 || status === 404 || status === 409;
    const shouldMarkGhost =
      notFoundHint ||
      String(errorCode || '').toLowerCase().includes('file_not_found') ||
      String(errorCode || '').toLowerCase().includes('missing');
    const canRetry = canRetryFlag === null ? !(isClientError || notFoundHint) : canRetryFlag;
    if (canRetry === false && shouldMarkGhost) {
      // File analysis is additive; avoid flipping source status to ghost here.
      // Ghosting is handled by dedicated vault validation paths.
      try {
        blockedFileAnalysisRef.current.add(String(fileId));
      } catch (_) {}
    }

    const prevEntry = fileAnalysisFailuresRef.current?.[fileId] || {};
    const attempts = Number(prevEntry.attempts || 0) + 1;
    const permanent = canRetry === false || attempts >= FILE_ANALYSIS_MAX_RETRIES;
    const backoffMs = Math.min(
      FILE_ANALYSIS_BACKOFF_BASE_MS * Math.pow(2, Math.max(0, attempts - 1)),
      8000
    );
    const nextRetryAt = !permanent ? Date.now() + backoffMs : null;

    setFileAnalysisFailures(prev => {
      return {
        ...prev,
        [fileId]: {
          attempts,
          lastError: message,
          lastErrorCode: errorCode,
          lastStatus: status || null,
          canRetry,
          permanent,
          nextRetryAt,
        },
      };
    });

    if (permanent) {
      try {
        blockedFileAnalysisRef.current.add(String(fileId));
      } catch (_) {}
    }

    if (canRetry !== false && !permanent) {
      scheduleAnalysisRetry(fileId, backoffMs);
    }
  }, [scheduleAnalysisRetry]);

  const isIgnorableFileAnalysisError = useCallback(error => {
    const errorData = error?.response?.data || error?.data || {};
    const status = Number(error?.response?.status || 0);
    const code = String(
      errorData?.code || errorData?.error || errorData?.reason || errorData?.status || ''
    ).toLowerCase();
    const message = String(
      errorData?.message || errorData?.error || error?.message || ''
    ).toLowerCase();
    const fileNotFound =
      message.includes('file not found in notebook') ||
      message.includes('file not found') ||
      code.includes('file_not_found') ||
      code.includes('missing');
    return (status === 400 || status === 404) && fileNotFound;
  }, []);

  const loadAnalysisData = useCallback(async () => {
    if (!notebook?.id) return;
    try {
      try {
        analysisAbortRef.current?.abort?.();
      } catch (_) {}
      const controller = new AbortController();
      analysisAbortRef.current = controller;

      if (!cancelRef.current) setAnalysisLoading(true);
      const res = await intelligenceStudioApi.getVaultAnalysis(notebook.id, { signal: controller.signal });
      if (cancelRef.current) return;
      const data = res?.data?.data || res?.data || res;
      if (data) setAnalysisData(data);
    } catch (e) {
      if (cancelRef.current || isCanceledRequest(e)) return;
      // Non-fatal: analysis may not exist yet
      console.warn('[Analysis] Failed to load:', e?.message || e);
    } finally {
      if (cancelRef.current) return;
      setAnalysisLoading(false);
    }
  }, [notebook?.id]);

  // ✅ ENTERPRISE: Manual trigger for post-ingestion analysis
  const runAnalysis = useCallback(
    async (force = false) => {
      if (!notebook?.id || analysisRunning) return;
      if (!cancelRef.current) setAnalysisRunning(true);
      try {
        try {
          runAnalysisAbortRef.current?.abort?.();
        } catch (_) {}
        const controller = new AbortController();
        runAnalysisAbortRef.current = controller;

        console.log(`🔄 [Analysis] Running analysis for vault: ${notebook.id}`);
        const res = await intelligenceStudioApi.runAnalysis(
          notebook.id,
          {
            force,
            includeCrossFile: (notebook?.files?.length || 0) >= 2,
          },
          { signal: controller.signal }
        );
        if (cancelRef.current) return;
        if (res?.data?.success || res?.success) {
          console.log(`✅ [Analysis] Completed successfully`);
          // Reload analysis data after running
          await loadAnalysisData();
        } else {
          console.warn(`⚠️ [Analysis] Failed:`, res?.data?.error || res?.error);
        }
      } catch (e) {
        if (cancelRef.current || isCanceledRequest(e)) return;
        console.error(`❌ [Analysis] Error:`, e?.message || e);
      } finally {
        if (cancelRef.current) return;
        setAnalysisRunning(false);
      }
    },
    [notebook?.id, notebook?.files?.length, analysisRunning, loadAnalysisData]
  );

  const runFileAnalysisNow = useCallback(
    async (fileId, force = false) => {
      if (!notebook?.id || !fileId || analysisRunning) return false;
      if (!cancelRef.current) setAnalysisRunning(true);
      try {
        try {
          runAnalysisAbortRef.current?.abort?.();
        } catch (_) {}
        const controller = new AbortController();
        runAnalysisAbortRef.current = controller;

        console.log(`🔄 [Analysis] Running file analysis for ${fileId}`);
        await intelligenceStudioApi.runFileAnalysis(notebook.id, fileId, { force });
        if (cancelRef.current) return false;
        await loadAnalysisData();
        clearFileAnalysisFailure(fileId);
        return true;
      } catch (e) {
        if (cancelRef.current || isCanceledRequest(e)) return false;
        if (isIgnorableFileAnalysisError(e)) {
          const errorData = e?.response?.data || e?.data || {};
          const msg = errorData?.message || errorData?.error || e?.message || 'File not found';
          console.warn(`⚠️ [Analysis] Optional file analysis skipped for ${fileId}: ${msg}`);
          try {
            blockedFileAnalysisRef.current.add(String(fileId));
          } catch (_) {}
          clearFileAnalysisFailure(fileId);
          return false;
        }
        console.error(`❌ [Analysis] File analysis error:`, e?.message || e);
        recordFileAnalysisFailure(fileId, e);
        return false;
      } finally {
        if (cancelRef.current) return;
        setAnalysisRunning(false);
      }
    },
    [
      notebook?.id,
      analysisRunning,
      loadAnalysisData,
      clearFileAnalysisFailure,
      recordFileAnalysisFailure,
      isIgnorableFileAnalysisError,
    ]
  );

  useEffect(() => {
    loadAnalysisData();
  }, [loadAnalysisData]);

  // ✅ ENTERPRISE: Auto-trigger analysis when vault is ready but no analysis exists
  // This ensures analysis runs even if automatic trigger from finalization didn't work
  useEffect(() => {
    // Only auto-trigger if:
    // 1. Vault is in a ready state (trusted/readable)
    // 2. No analysis data exists yet
    // 3. Not already running or loading
    const hasAnalysisData =
      analysisData?.fileAnalyses && Object.keys(analysisData.fileAnalyses).length > 0;
    const shouldAutoTrigger =
      (notebookTrustState === 'trusted' || notebookTrustState === 'readable') &&
      !hasAnalysisData &&
      !analysisRunning &&
      !analysisLoading &&
      notebook?.files?.length > 0;

    if (!shouldAutoTrigger) return;

    // Check session storage to avoid re-triggering on every render
    const autoTriggerKey = `analysis_auto_trigger_${notebook?.id}`;
    const alreadyTriggered = sessionStorage.getItem(autoTriggerKey);

    if (alreadyTriggered) {
      // Already triggered for this session - just poll for results
      let pollCount = 0;
      const maxPolls = 15;
      const pollInterval = setInterval(() => {
        pollCount++;
        console.log(`[Analysis] Polling for results (${pollCount}/${maxPolls})...`);
        loadAnalysisData();

        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          console.log(
            `[Analysis] Poll stopped after ${maxPolls} attempts - user can manually trigger`
          );
        }
      }, 4000);

      return () => clearInterval(pollInterval);
    }

    // Auto-trigger analysis
    console.log(`⚡ [Analysis] Auto-triggering analysis for vault: ${notebook?.id}`);
    sessionStorage.setItem(autoTriggerKey, 'triggered');
    runAnalysis(false);

    // No cleanup needed as runAnalysis handles its own state
  }, [
    notebookTrustState,
    analysisData,
    analysisRunning,
    analysisLoading,
    notebook?.id,
    notebook?.files?.length,
    loadAnalysisData,
    runAnalysis,
  ]);

  // ✅ ENTERPRISE: If a READY file has no analysis yet, run per-file analysis once.
  useEffect(() => {
    if (!notebook?.id) return;
    if (analysisRunning || analysisLoading) return;

    const readyFiles = files.filter(f => {
      const status = String(f?.lifecycleSnapshot?.status || f?.status || '').toLowerCase();
      return status === 'ready' || status === 'ready_with_warnings';
    });
    if (readyFiles.length === 0) return;

    const missing = readyFiles.filter(f => {
      const fileId = String(f?.fileId || f?.id || f?._id || '');
      if (!fileId) return false;
      if (isGhostFile(f)) return false;
      if (blockedFileAnalysisRef.current.has(fileId)) return false;
      const failure = fileAnalysisFailures[fileId];
      if (failure?.permanent) return false;
      if (Number(failure?.attempts || 0) >= FILE_ANALYSIS_MAX_RETRIES) return false;
      return !analysisData?.fileAnalyses?.[fileId];
    });
    if (missing.length === 0) return;

    const target = missing[0];
    const fileId = String(target?.fileId || target?.id || target?._id || '');
    if (!fileId) return;
    if (blockedFileAnalysisRef.current.has(fileId)) return;

    const autoKey = `analysis_file_auto_${notebook.id}_${fileId}`;
    const failure = fileAnalysisFailures[fileId] || null;
    const now = Date.now();
    const retryAt = typeof failure?.nextRetryAt === 'number' ? failure.nextRetryAt : null;
    const retryEligible = failure && !failure.permanent && (!retryAt || now >= retryAt);
    const cached = (() => {
      try {
        return JSON.parse(sessionStorage.getItem(autoKey) || 'null');
      } catch (_) {
        return null;
      }
    })();
    const cachedAttempts = Number(cached?.attempts || 0);
    const attemptCount = Math.max(cachedAttempts, Number(failure?.attempts || 0));
    if (cached && !retryEligible) return;
    if (attemptCount >= FILE_ANALYSIS_MAX_RETRIES && !retryEligible) return;

    console.log(`⚡ [Analysis] Auto-triggering file analysis for ${fileId}`);
    try {
      sessionStorage.setItem(
        autoKey,
        JSON.stringify({
          attempts: attemptCount + 1,
          lastAttemptAt: now,
        })
      );
    } catch (_) {}
    runFileAnalysisNow(fileId, false);
  }, [
    notebook?.id,
    files,
    analysisData,
    analysisRunning,
    analysisLoading,
    analysisRetryEpoch,
    fileAnalysisFailures,
    loadAnalysisData,
    runFileAnalysisNow,
  ]);

  // Get file analysis from cached data
  const getFileAnalysisData = useCallback(
    fileId => {
      if (!analysisData?.fileAnalyses) return null;
      return analysisData.fileAnalyses[fileId] || null;
    },
    [analysisData]
  );

  const upsertMemory = useCallback(
    async payload => {
      if (!notebook?.id) return null;
      try {
        const res = await intelligenceStudioApi.upsertInsightMemory(notebook.id, payload);
        const outer = res?.data?.data || res?.data || res;
        const entry = outer && outer.success === true && outer.data ? outer.data : outer;
        if (entry && entry.id) {
          setInsightMemory(prev => {
            const arr = Array.isArray(prev) ? prev : [];
            const idx = arr.findIndex(
              m =>
                String(m?.id) === String(entry.id) ||
                String(m?.memoryKey) === String(entry.memoryKey)
            );
            if (idx >= 0) {
              const next = [...arr];
              next[idx] = entry;
              return next;
            }
            return [...arr, entry];
          });
          return entry;
        }
      } catch (e) {
        console.warn('[InsightMemory] Upsert failed:', e?.message || e);
      }
      return null;
    },
    [notebook?.id]
  );

  const recordMemoryEvent = useCallback(
    async (memoryIdOrKey, event) => {
      if (!notebook?.id) return;
      if (!memoryIdOrKey) return;
      try {
        const res = await intelligenceStudioApi.recordInsightMemoryEvent(
          notebook.id,
          String(memoryIdOrKey),
          event
        );
        const outer = res?.data?.data || res?.data || res;
        const entry = outer && outer.success === true && outer.data ? outer.data : outer;
        if (entry && entry.id) {
          setInsightMemory(prev => {
            const arr = Array.isArray(prev) ? prev : [];
            const idx = arr.findIndex(m => String(m?.id) === String(entry.id));
            if (idx < 0) return arr;
            const next = [...arr];
            next[idx] = entry;
            return next;
          });
        }
      } catch (e) {
        // Ignore: telemetry-like
      }
    },
    [notebook?.id]
  );

  // ✅ Dopamine loop: show “recognition” micro-wins while processing (derived from nano-metadata, not guessed).
  const recognition = useMemo(() => {
    if (notebookTrustState !== 'processing') return [];
    const out = [];
    for (const f of files) {
      const name = getFileName(f);
      const micro =
        (typeof f?.nanoMetadata?.details?.micro_insight === 'string' &&
          f.nanoMetadata.details.micro_insight.trim()) ||
        (typeof f?.nanoMetadata?.details?.microInsight === 'string' &&
          f.nanoMetadata.details.microInsight.trim()) ||
        '';
      if (!micro) continue;
      out.push({ name, micro });
      if (out.length >= 4) break;
    }
    return out;
  }, [files, notebookTrustState]);

  // ✅ Addiction layer: "Proactive Pounce" when a file flips to Trusted (system speaks first).
  const prevTrustByFileIdRef = useRef(new Map());
  const prevNotebookTrustRef = useRef(null);
  const lastPounceAtRef = useRef(0);
  const [pounces, setPounces] = useState([]); // { id, kind: 'file'|'all', fileId?, title, message, actions?: [{label,prompt}] }

  const buildFilePounce = (file, nextState) => {
    const fileName = getFileName(file);
    const ext = getFileExt(fileName);
    const nano = file?.nanoMetadata?.details || file?.nanoMetadata?.details || null;
    const brief = typeof file?.autoBrief === 'string' ? file.autoBrief.trim() : '';
    const micro =
      (typeof nano?.micro_insight === 'string' && nano.micro_insight.trim()) ||
      (typeof nano?.microInsight === 'string' && nano.microInsight.trim()) ||
      '';

    const bestFinding = pickBestFinding(file);
    const findingText = bestFinding ? String(bestFinding.text || '').trim() : '';

    // Score: prioritize citable/high-certainty findings; then file-type hints.
    let score = bestFinding?._score || 0;
    const isExcel =
      ['xls', 'xlsx', 'csv'].includes(ext) || nano?.is_financial === true || nano?.sheet_count > 0;
    if (
      !bestFinding &&
      isExcel &&
      (nano?.is_financial || nano?.has_date_column || nano?.numeric_column_count > 0)
    ) {
      score = 70;
    }
    if (!bestFinding && brief) {
      const firstLine =
        brief
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)[0] || '';
      if (firstLine.length >= 40) score = Math.max(score, 68);
      else if (firstLine.length >= 20) score = Math.max(score, 62);
    }
    if (!bestFinding && micro) score = Math.max(score, 45);

    // If score is too low, prefer silence.
    if (score < 60) return null;

    const title = `${nextState === 'trusted' ? 'Trusted' : 'Readable'}: ${fileName}`;

    // Message templates (value-first)
    let message = '';
    const actions = [];

    if (bestFinding && findingText) {
      const cert = normalizeCertainty(bestFinding.certainty);
      message = `${cert === 'high' ? 'Locked onto' : 'Noticed'}: **${findingText}**`;
      actions.push(
        {
          label: 'Show evidence',
          prompt: `Show evidence (with citations) for: ${findingText}`,
        },
        {
          label: 'Explain implications',
          prompt: `Explain the implications of: ${findingText}`,
        }
      );
    } else if (isExcel) {
      const sheetCount = nano?.sheet_count || nano?.sheetCount || file?.sheetCount || 0;
      const totalRows = nano?.total_rows || nano?.totalRows || file?.rowCount || 0;
      const isFinancial = nano?.is_financial === true;
      const hasTimeSeries = nano?.has_date_column === true && (nano?.numeric_column_count || 0) > 0;
      message =
        (isFinancial ? 'Financial spreadsheet detected. ' : 'Spreadsheet detected. ') +
        (hasTimeSeries ? 'It looks time-series capable. ' : '') +
        (sheetCount ? `${sheetCount} sheet${sheetCount === 1 ? '' : 's'}. ` : '') +
        (totalRows ? `~${totalRows} rows. ` : '') +
        (nextState === 'trusted'
          ? 'Want me to surface anomalies, trends, or mismatches against other sources?'
          : 'Want me to surface a quick brief and the top anomalies (verify key numbers)?');

      actions.push(
        { label: 'Find anomalies', prompt: `Find anomalies and outliers in @${fileName}` },
        { label: 'Summarize metrics', prompt: `Summarize key metrics and trends in @${fileName}` }
      );
    } else if (micro) {
      message = `${micro} Want a 5-bullet brief with citations?`;
      actions.push({
        label: 'Brief it',
        prompt: `Give me a 5-bullet executive brief (with citations) for @${fileName}`,
      });
    } else if (brief) {
      const firstLine =
        brief
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)[0] || brief.slice(0, 220);
      message = `Key takeaway: **${firstLine}**`;
      actions.push(
        { label: 'Verify', prompt: `Verify this with citations: ${firstLine}` },
        {
          label: 'Ask follow-ups',
          prompt: `What are the top follow-up questions for @${fileName}?`,
        }
      );
    } else {
      message =
        'This source just became Trusted. Want a quick brief or a targeted check (dates, numbers, obligations)?';
      actions.push({
        label: 'Quick brief',
        prompt: `Quick brief (with citations) for @${fileName}`,
      });
    }

    // "Why it mattered" (for Insight Memory continuity)
    const whyMattered =
      bestFinding && findingText
        ? 'High-signal finding with evidence anchors (best available for reliable verification).'
        : isExcel
          ? 'Spreadsheets can hide mismatches and outliers—surfacing anomalies early saves review cycles.'
          : micro
            ? 'This changes how confidently we can rely on extraction/preview for downstream analysis.'
            : brief
              ? 'A stable takeaway is available—use it to drive targeted verification instead of re-reading.'
              : 'This source crossed the reliability threshold, unlocking trustworthy operations.';

    return { title, message, actions, whyMattered };
  };

  useEffect(() => {
    const nextMap = new Map();

    for (const f of files) {
      const fileId = f?.fileId || f?.id || f?._id;
      if (!fileId) continue;
      const id = String(fileId);
      const nextState = getEffectiveTrustState(f);
      nextMap.set(id, nextState);

      const prevState = prevTrustByFileIdRef.current.get(id) || null;
      if (
        prevState &&
        !['trusted', 'readable'].includes(prevState) &&
        (nextState === 'trusted' || nextState === 'readable')
      ) {
        // Rate-limit pounces so they feel intentional (earned interruption).
        if (Date.now() - lastPounceAtRef.current < 4500) continue;

        const built = buildFilePounce(f, nextState);
        if (!built) continue;

        lastPounceAtRef.current = Date.now();
        setPounces(prev => {
          const pId = `file:${id}`;
          const existingIdx = prev.findIndex(p => p.id === pId);
          if (existingIdx >= 0) {
            const existing = prev[existingIdx];
            const isUpgrade =
              String(existing?.title || '').startsWith('Readable:') &&
              String(built?.title || '').startsWith('Trusted:');
            if (!isUpgrade) return prev;
            const next = [...prev];
            next[existingIdx] = {
              ...existing,
              title: built.title,
              message: built.message,
              actions: built.actions,
            };
            return next;
          }
          return [
            {
              id: pId,
              kind: 'file',
              fileId: id,
              title: built.title,
              message: built.message,
              actions: built.actions,
              whyMattered: built.whyMattered,
              memoryKey: `pounce:file:${id}`,
              memoryId: null,
            },
            ...prev,
          ];
        });

        // Persist to Insight Memory (non-blocking)
        upsertMemory({
          memoryKey: `pounce:file:${id}`,
          scope: 'file',
          fileId: id,
          trustStateAt: nextState,
          title: built.title,
          whatFound: built.message,
          whyMattered: built.whyMattered,
          suggestedActions: built.actions,
          source: 'proactive_pounce_v1',
        }).then(entry => {
          if (!entry?.id) return;
          setPounces(prev =>
            prev.map(p => (p?.memoryKey === `pounce:file:${id}` ? { ...p, memoryId: entry.id } : p))
          );
        });
      }
    }

    // Vault-level "victory" pounce when everything becomes Trusted.
    const prevNotebook = prevNotebookTrustRef.current;
    if (prevNotebook && prevNotebook !== 'trusted' && notebookTrustState === 'trusted') {
      if (Date.now() - lastPounceAtRef.current < 4500) {
        // Avoid double-pouncing if a file just unlocked.
      } else {
        const text = typeof summaryText === 'string' ? summaryText.trim() : '';
        const firstLine = text
          ? text
              .split('\n')
              .map(s => s.trim())
              .filter(Boolean)[0]
          : '';
        setPounces(prev => {
          const pId = 'all:trusted';
          if (prev.some(p => p.id === pId)) return prev;
          return [
            {
              id: pId,
              kind: 'all',
              title: 'Vault is now Trusted',
              message:
                firstLine ||
                'Unified synthesis is unlocked. Want a cross-document risk scan or a timeline?',
              actions: [
                { label: 'Risk scan', prompt: 'Run a cross-document risk scan (with citations).' },
                {
                  label: 'Timeline',
                  prompt: 'Build a timeline across all documents (with citations).',
                },
              ],
              whyMattered:
                'Vault-level operations are now safe to run across sources (synthesis, conflicts, timelines).',
              memoryKey: 'pounce:all:trusted',
              memoryId: null,
            },
            ...prev,
          ];
        });
        lastPounceAtRef.current = Date.now();

        upsertMemory({
          memoryKey: 'pounce:all:trusted',
          scope: 'vault',
          fileId: null,
          trustStateAt: 'trusted',
          title: 'Vault is now Trusted',
          whatFound: firstLine || 'Vault reached Trusted state.',
          whyMattered:
            'Vault-level operations are now safe to run across sources (synthesis, conflicts, timelines).',
          suggestedActions: [
            { label: 'Risk scan', prompt: 'Run a cross-document risk scan (with citations).' },
            {
              label: 'Timeline',
              prompt: 'Build a timeline across all documents (with citations).',
            },
          ],
          source: 'vault_victory_v1',
        }).then(entry => {
          if (!entry?.id) return;
          setPounces(prev =>
            prev.map(p =>
              p?.memoryKey === 'pounce:all:trusted' ? { ...p, memoryId: entry.id } : p
            )
          );
        });
      }
    }

    prevNotebookTrustRef.current = notebookTrustState;
    prevTrustByFileIdRef.current = nextMap;
  }, [files, notebookTrustState, summaryText]);

  const tabItems = useMemo(() => {
    const base = isSingleFileMode
      ? []
      : [
          {
            id: 'all',
            label: 'All Documents',
            icon: <Layers className="h-4 w-4" />,
          },
        ];
    const fileTabs = files
      .map(f => {
        const fileId = f.fileId || f.id || f._id;
        if (!fileId) return null;
        const trustState = getEffectiveTrustState(f);
        // ✅ Delayed reveal: per-file tabs appear once the file exits Processing (unless single-file mode).
        if (!isSingleFileMode && trustState === 'processing') return null;
        const fileName = f.originalName || f.name || f.filename || 'Document';
        return {
          id: String(fileId),
          label: fileName,
          fileName,
        };
      })
      .filter(Boolean);
    const combined = isSingleFileMode ? fileTabs : [...base, ...fileTabs];
    return combined.length > 0 ? combined : base;
  }, [files, isSingleFileMode]);

  const [activeTab, setActiveTab] = useState(() =>
    tabItems[0]?.id ? String(tabItems[0].id) : 'all'
  );
  const prevTabIdsRef = useRef(new Set());
  const [justUnlockedTabIds, setJustUnlockedTabIds] = useState([]); // string[]
  const [tabsWithNewAnalysis, setTabsWithNewAnalysis] = useState(new Set()); // ✅ Track tabs that got new analysis
  const prevAnalysisKeysRef = useRef(new Set()); // Track which files had analysis
  const [showReadyCelebration, setShowReadyCelebration] = useState(false); // ✅ TRUST UX: Celebration moment
  const celebrationShownRef = useRef(false); // Only show once per session

  // ✅ TRUST UX: Detect "Notebook Ready" moment and show celebration
  useEffect(() => {
    // Skip if already shown this session
    if (celebrationShownRef.current) return;

    const readiness = getNotebookReadiness(files, analysisData);

    // Show celebration when notebook becomes fully ready
    if (readiness.ready && files.length > 0) {
      // Check sessionStorage to ensure one-time per notebook
      const celebrationKey = `notebook_ready_shown_${notebook?.id}`;
      if (!sessionStorage.getItem(celebrationKey)) {
        console.log(`🎉 [Trust UX] Notebook ready celebration triggered`);
        setShowReadyCelebration(true);
        sessionStorage.setItem(celebrationKey, 'shown');
        celebrationShownRef.current = true;

        // Auto-hide after 8 seconds
        setTimeout(() => {
          setShowReadyCelebration(false);
        }, 8000);
      }
    }
  }, [files, analysisData, notebook?.id]);

  // ✅ ENTERPRISE: Detect when analysis is newly available and highlight those tabs
  useEffect(() => {
    if (!analysisData?.fileAnalyses) return;

    const currentKeys = new Set(Object.keys(analysisData.fileAnalyses));
    const prevKeys = prevAnalysisKeysRef.current;

    // Find newly analyzed files
    const newlyAnalyzed = Array.from(currentKeys).filter(
      k => !prevKeys.has(k) && analysisData.fileAnalyses[k]?.analysis
    );

    if (newlyAnalyzed.length > 0) {
      console.log(`✨ [Analysis] New analysis ready for: ${newlyAnalyzed.join(', ')}`);
      setTabsWithNewAnalysis(prev => new Set([...prev, ...newlyAnalyzed]));

      // Clear highlight after 5 seconds
      setTimeout(() => {
        setTabsWithNewAnalysis(prev => {
          const next = new Set(prev);
          newlyAnalyzed.forEach(k => next.delete(k));
          return next;
        });
      }, 5000);
    }

    prevAnalysisKeysRef.current = currentKeys;
  }, [analysisData]);

  // ✅ Delayed reveal unlock animation (earned)
  useEffect(() => {
    const prev = prevTabIdsRef.current || new Set();
    const next = new Set((tabItems || []).map(t => String(t.id)));
    const newlyUnlocked = Array.from(next).filter(id => id !== 'all' && !prev.has(id));

    prevTabIdsRef.current = next;

    if (newlyUnlocked.length === 0) return undefined;

    setJustUnlockedTabIds(newlyUnlocked);
    const t = setTimeout(() => setJustUnlockedTabIds([]), 1200);
    t?.unref?.();
    return () => clearTimeout(t);
  }, [tabItems]);

  const activeFile = useMemo(() => {
    if (activeTab === 'all') return null;
    return files.find(f => String(f.fileId || f.id || f._id) === String(activeTab)) || null;
  }, [activeTab, files]);

  // If a tab disappears due to reprocessing (tab reveal is trust-driven), fall back safely.
  useEffect(() => {
    const ids = new Set((tabItems || []).map(t => String(t.id)));
    if (!ids.has(String(activeTab))) {
      const fallback = tabItems?.[0]?.id ? String(tabItems[0].id) : 'all';
      setActiveTab(fallback);
    }
  }, [tabItems, activeTab]);

  const renderAll = () => {
    const text = typeof summaryText === 'string' ? summaryText.trim() : '';
    const analyzed =
      typeof notebook?.userTrustSummary?.analyzed === 'number'
        ? notebook.userTrustSummary.analyzed
        : null;
    const total =
      typeof notebook?.userTrustSummary?.total === 'number'
        ? notebook.userTrustSummary.total
        : null;
    const insightCount = Array.isArray(insightMemory) ? insightMemory.length : 0;

    const allGate =
      notebookTrustState === 'low_quality'
        ? {
            tone: 'red',
            title: 'Unified synthesis is blocked',
            body: 'At least one source is Low Quality. Fix the source(s) in the Sources panel to unlock reliable cross-document synthesis.',
          }
        : notebookTrustState === 'restoring'
          ? {
              // ✅ CACHE FIX: Restoring is fast - show optimistic message
              tone: 'blue',
              title: 'Restoring from cache...',
              body: "These files are already in my memory. I'm preparing them for this vault — this should only take a moment.",
            }
          : // 🔒 PRODUCT LAW: No “analyzing / intelligence building” narration.
            // If processing is still happening, the Sources panel already shows truthful per-file state.
            null;
    // NOTE: we intentionally avoid a generic processing banner to prevent contradictions.
    return (
      <div className="space-y-3">
        {/* Vault identity (Narrative) */}
        {vaultNarrativeText &&
          (notebookTrustState === 'trusted' || notebookTrustState === 'readable') && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 p-4 text-sm text-slate-700 dark:text-slate-200">
              <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                <span>✨</span>
                <span>Vault identity</span>
              </div>
              <div className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                <MarkdownText content={vaultNarrativeText} />
              </div>
            </div>
          )}

        {/* Auto-surfaced contradictions (authoritative graph engine) */}
        <ContradictionsAutoSurface
          notebookId={notebook?.id}
          notebookTrustState={notebookTrustState}
          fileCount={files.length}
          onQuickAsk={onQuickAsk}
        />

        {pounces.filter(p => p.kind === 'all' || p.kind === 'file').length > 0 && (
          <div className="space-y-2">
            {pounces
              .filter(p => p.kind === 'all' || p.kind === 'file')
              .slice(0, 3)
              .map(p => (
                <div
                  key={p.id}
                  className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-100"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-semibold">{p.title}</div>
                    <button
                      type="button"
                      onClick={() => {
                        setPounces(prev => prev.filter(x => x.id !== p.id));
                        recordMemoryEvent(p.memoryId || p.memoryKey, { type: 'dismissed' });
                      }}
                      className="text-[11px] font-semibold px-2 py-1 rounded-full border border-amber-300/60 dark:border-amber-800/60 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                      title="Dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                  <div className="opacity-90">
                    <MarkdownText content={p.message} />
                  </div>
                  {onQuickAsk && Array.isArray(p.actions) && p.actions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.actions.slice(0, 3).map(a => (
                        <button
                          key={`${p.id}:${a.label}`}
                          type="button"
                          onClick={() => {
                            recordMemoryEvent(p.memoryId || p.memoryKey, {
                              type: 'clicked',
                              actionLabel: a.label,
                              prompt: a.prompt,
                            });
                            onQuickAsk(String(a.prompt || '').trim());
                          }}
                          className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-white/80 dark:bg-neutral-900/30 border border-amber-200 dark:border-amber-800 hover:bg-white dark:hover:bg-neutral-900/50 transition-colors"
                          title={a.prompt}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}

        {/* Quiet state: no proactive highlights */}
        {(notebookTrustState === 'trusted' || notebookTrustState === 'readable') &&
          pounces.filter(p => p.kind === 'all' || p.kind === 'file').length === 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4 text-sm text-slate-700 dark:text-slate-200">
              <div className="font-semibold">No new highlights yet</div>
              <div className="opacity-90 mt-1">
                Nothing urgent needs attention right now. You can build a timeline, compare
                documents, or run a targeted check at any time.
              </div>
            </div>
          )}

        {(insightCount > 0 || memoryLoading) && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 overflow-hidden">
            <button
              type="button"
              onClick={() => setMemoryOpen(v => !v)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Clock className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
                <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                  Saved highlights
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-600 dark:text-neutral-300">
                  {insightCount}
                </span>
                {memoryLoading && (
                  <span className="text-[11px] text-slate-400 dark:text-neutral-500">Loading…</span>
                )}
              </div>
              {memoryOpen ? (
                <ChevronUp className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
              )}
            </button>
            {memoryOpen && (
              <div className="px-4 pb-4 space-y-3">
                {(Array.isArray(insightMemory) ? insightMemory : [])
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b?.updatedAt || b?.createdAt || 0) -
                      new Date(a?.updatedAt || a?.createdAt || 0)
                  )
                  .slice(0, 10)
                  .map(m => (
                    <div
                      key={m.id || m.memoryKey}
                      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 p-3 text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-slate-900 dark:text-white">
                          {m.title || 'Insight'}
                        </div>
                        <div className="text-[11px] text-slate-400 dark:text-neutral-500">
                          {m.updatedAt ? new Date(m.updatedAt).toLocaleString() : null}
                        </div>
                      </div>
                      {m.whatFound && (
                        <div className="mt-2 text-slate-700 dark:text-slate-200">
                          <MarkdownText content={String(m.whatFound)} />
                        </div>
                      )}
                      {m.whyMattered && (
                        <div className="mt-2 text-[12px] text-slate-600 dark:text-neutral-300">
                          <span className="font-semibold">Why it mattered:</span> {m.whyMattered}
                        </div>
                      )}
                      {onQuickAsk &&
                        Array.isArray(m.suggestedActions) &&
                        m.suggestedActions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {m.suggestedActions.slice(0, 3).map(a => (
                              <button
                                key={`${m.id}:${a.label}`}
                                type="button"
                                onClick={() => {
                                  recordMemoryEvent(m.id || m.memoryKey, {
                                    type: 'clicked',
                                    actionLabel: a.label,
                                    prompt: a.prompt,
                                  });
                                  onQuickAsk(String(a.prompt || '').trim());
                                }}
                                className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-white dark:bg-neutral-900/30 border border-slate-200 dark:border-neutral-700 hover:bg-slate-50 dark:hover:bg-neutral-900/50 transition-colors"
                                title={a.prompt}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-700 dark:text-amber-300">
            <Layers className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              {resolvedSummaryTitle}
            </div>
            {/* ✅ ENTERPRISE V2: REMOVED "Progress: X/Y analyzed" display
                REASON: Misleading when X=0 - implies ongoing processing when vault is already ready.
                AI enrichment status is shown per-file in trust badges and tabs.
            */}
          </div>
        </div>

        {allGate ? (
          <div
            className={`rounded-xl border p-4 text-sm ${
              allGate.tone === 'red'
                ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
            }`}
          >
            <div className="font-semibold mb-1">{allGate.title}</div>
            <div className="opacity-90">{allGate.body}</div>
            {allGate.tone === 'amber' && recognition.length > 0 && (
              <div className="mt-3 text-[12px]">
                <div className="font-semibold mb-1">What I’ve already recognized</div>
                <ul className="list-disc pl-5 space-y-1 opacity-95">
                  {recognition.map(r => (
                    <li key={`${r.name}:${r.micro}`}>
                      <span className="font-semibold">{r.name}:</span> {r.micro}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : text ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
            <MarkdownText content={text} />
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
            {/* ✅ TRUST UX: Status-based messaging (NO action buttons) */}
            {analysisLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <Clock className="h-4 w-4 animate-pulse" />
                Loading analysis...
              </div>
            ) : analysisRunning ? (
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <Clock className="h-4 w-4 animate-pulse" />
                AI analysis is running in the background. This may take a minute...
              </div>
            ) : (
              <div className="text-sm text-slate-600 dark:text-slate-300">
                Analysis will run automatically after ingestion completes.
              </div>
            )}
          </div>
        )}

        {/* Cross-File Analysis (if available) */}
        {analysisData?.crossFileAnalysis && (
          <div className="mt-4 space-y-4">
            {analysisData.crossFileAnalysis.crossFileInsights?.contradictions?.length > 0 && (
              <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
                    Cross-Document Contradictions
                  </span>
                </div>
                <ul className="space-y-3">
                  {analysisData.crossFileAnalysis.crossFileInsights.contradictions
                    .slice(0, 5)
                    .map((c, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-purple-800 dark:text-purple-200 border-b border-purple-100 dark:border-purple-800 pb-2 last:border-0"
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              c.severity === 'critical'
                                ? 'bg-red-600 text-white'
                                : c.severity === 'high'
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-yellow-500 text-black'
                            }`}
                          >
                            {c.severity}
                          </span>
                          <span>{c.contradiction}</span>
                        </div>
                        <div className="mt-1 text-xs text-purple-600 dark:text-purple-300">
                          {c.file1?.fileName} ↔ {c.file2?.fileName}
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {analysisData.crossFileAnalysis.crossFileInsights?.synthesis?.narrative && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="h-4 w-4 text-slate-600" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    Cross-Document Synthesis
                  </span>
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  <MarkdownText
                    content={analysisData.crossFileAnalysis.crossFileInsights.synthesis.narrative}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFile = file => {
    const fileName = file?.originalName || file?.name || file?.filename || 'Document';
    const trust = file?.userTrust || null;
    const trustState = getEffectiveTrustState(file);
    const ghostFile = isGhostFile(file);
    const fileBriefingLabel = isSingleFileMode ? 'Document summary' : 'File briefing (per document)';

    const brief = typeof file?.autoBrief === 'string' ? file.autoBrief.trim() : '';

    return (
      <div className="space-y-3">
        {file?.fileId || file?.id || file?._id ? (
          <>
            {pounces.filter(
              p =>
                p.kind === 'file' && String(p.fileId) === String(file.fileId || file.id || file._id)
            ).length > 0 && (
              <div className="space-y-2">
                {pounces
                  .filter(
                    p =>
                      p.kind === 'file' &&
                      String(p.fileId) === String(file.fileId || file.id || file._id)
                  )
                  .slice(0, 1)
                  .map(p => (
                    <div
                      key={p.id}
                      className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-100"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-semibold">{p.title}</div>
                        <button
                          type="button"
                          onClick={() => {
                            setPounces(prev => prev.filter(x => x.id !== p.id));
                            recordMemoryEvent(p.memoryId || p.memoryKey, { type: 'dismissed' });
                          }}
                          className="text-[11px] font-semibold px-2 py-1 rounded-full border border-amber-300/60 dark:border-amber-800/60 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                          title="Dismiss"
                        >
                          Dismiss
                        </button>
                      </div>
                      <div className="opacity-90">
                        <MarkdownText content={p.message} />
                      </div>
                      {onQuickAsk && Array.isArray(p.actions) && p.actions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {p.actions.slice(0, 3).map(a => (
                            <button
                              key={`${p.id}:${a.label}`}
                              type="button"
                              onClick={() => {
                                recordMemoryEvent(p.memoryId || p.memoryKey, {
                                  type: 'clicked',
                                  actionLabel: a.label,
                                  prompt: a.prompt,
                                });
                                onQuickAsk(String(a.prompt || '').trim());
                              }}
                              className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-white/80 dark:bg-neutral-900/30 border border-amber-200 dark:border-amber-800 hover:bg-white dark:hover:bg-neutral-900/50 transition-colors"
                              title={a.prompt}
                            >
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </>
        ) : null}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <TabIcon fileName={fileName} className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div
                className="text-sm font-semibold text-slate-900 dark:text-white truncate"
                dir="auto"
              >
                {fileName}
              </div>
              <div className="text-xs text-slate-500 dark:text-neutral-400">
                {fileBriefingLabel}
              </div>
            </div>
          </div>
          <TrustPill trust={trust} />
        </div>

        {trustState === 'processing' && !ghostFile && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4 text-sm text-slate-700 dark:text-slate-200">
            This document is still preparing. The summary will appear when it’s ready.
          </div>
        )}

        {ghostFile && (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
            Upload incomplete — this file exists in storage but was not fully registered. Remove it
            and re-upload to process.
          </div>
        )}

        {trustState === 'low_quality' && (
          <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-200">
            {trust?.tooltip ||
              'Scan is too blurry for accurate analysis. Please upload a clearer version.'}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            ✅ AUTHORITY: Universal Authoritative Document Detection
            Content-driven, NOT filename-driven
            ═══════════════════════════════════════════════════════════════════ */}
        {(() => {
          const fileId = file?.fileId || file?.id || file?._id;
          const analysisResult = getFileAnalysisData(fileId);
          const authorityEntity = analysisResult?.analysis?.authorityEntity;
          const authorityDetection = analysisResult?.analysis?.authorityDetection;

          // AUTHORITY_DOMINANT: Show Authority Overview as primary (no strategic analysis)
          if (authorityEntity && authorityEntity.mode === 'AUTHORITY_DOMINANT') {
            return <AuthorityOverview authorityEntity={authorityEntity} file={file} />;
          }

          // AUTHORITY_NESTED: Show nested authority card, then continue with normal analysis
          if (authorityEntity && authorityEntity.mode === 'AUTHORITY_NESTED') {
            return <NestedAuthorityEntities authorityEntity={authorityEntity} />;
          }

          // AUTHORITY_SUSPECTED: Show soft hint (content-based, not filename-based)
          if (authorityDetection?.mode === 'AUTHORITY_SUSPECTED') {
            return (
              <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                    Official Document Detected
                  </span>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  This document contains official content. Full analysis has been applied, and
                  relevant structured data is being captured when possible.
                </p>
              </div>
            );
          }

          // Legacy: Support old identityEntity format for backward compatibility
          const legacyIdentity = analysisResult?.analysis?.identityEntity;
          if (legacyIdentity && legacyIdentity.mode === 'IDENTITY_DOMINANT') {
            return (
              <AuthorityOverview authorityEntity={legacyIdentity} file={file} isLegacy={true} />
            );
          }
          if (legacyIdentity && legacyIdentity.mode === 'IDENTITY_NESTED') {
            return <NestedAuthorityEntities authorityEntity={legacyIdentity} isLegacy={true} />;
          }

          return null;
        })()}

        {/* ═══════════════════════════════════════════════════════════════════
            ✅ ENTERPRISE: Display Analysis Results from PostIngestionAnalysisEngine
            (Skip for AUTHORITY_DOMINANT files - they don't get strategic analysis)
            ═══════════════════════════════════════════════════════════════════ */}
        {(() => {
          const fileId = file?.fileId || file?.id || file?._id;
          const analysisResult = getFileAnalysisData(fileId);
          const analysis = analysisResult?.analysis;

          // Skip strategic analysis for authority-dominant documents (no speculation allowed)
          if (analysis?.authorityEntity?.mode === 'AUTHORITY_DOMINANT') {
            return null;
          }
          // Legacy: Also check old identityEntity format
          if (analysis?.identityEntity?.mode === 'IDENTITY_DOMINANT') {
            return null;
          }

          if (analysis && analysis.executiveSummary) {
            // Determine confidence level for this file's analysis
            const confidenceLevel = getFileConfidenceLevel(file, analysisResult);
            const confidenceBadge = getConfidenceBadge(confidenceLevel);
            const ConfidenceIcon = confidenceBadge.icon;
            const isMetadataOnly =
              analysis.textSource === 'metadata_fallback' || analysis.isMetadataOnly === true;

            return (
              <div className="space-y-4">
                {/* ✅ TRUST UX: Metadata-only notice (when applicable) */}
                {isMetadataOnly && (
                  <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <div className="flex items-start gap-2">
                      <FileQuestion className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-xs font-medium text-amber-800 dark:text-amber-200">
                          Partial analysis based on metadata
                        </div>
                        <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                          Full text extraction was limited for this file. Analysis is based on
                          structural metadata and inferred patterns.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Executive Summary (UPGRADED V2) */}
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        Executive Summary
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* ✅ TRUST UX: Confidence badge */}
                      <div
                        className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border ${confidenceBadge.className}`}
                        title={confidenceBadge.tooltip}
                      >
                        <ConfidenceIcon className="h-3 w-3" />
                        <span>{confidenceBadge.label}</span>
                      </div>
                      {analysis.analysisEngine && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-500 dark:text-neutral-400">
                          {analysis.analysisEngine}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                    {analysis.executiveSummary.headline}
                  </div>
                  {/* Strategic Implications (V2) */}
                  {analysis.executiveSummary.strategicImplications && (
                    <div className="text-sm text-slate-700 dark:text-slate-200 mb-3 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border-l-2 border-amber-500">
                      <strong className="text-amber-700 dark:text-amber-400">
                        Strategic View:
                      </strong>{' '}
                      {analysis.executiveSummary.strategicImplications}
                    </div>
                  )}
                  {Array.isArray(analysis.executiveSummary.keyPoints) &&
                    analysis.executiveSummary.keyPoints.length > 0 && (
                      <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                        {analysis.executiveSummary.keyPoints.slice(0, 5).map((kp, idx) => (
                          <li
                            key={idx}
                            className="pl-3 border-l-2 border-slate-200 dark:border-slate-700"
                          >
                            <div className="font-medium">{kp.point}</div>
                            {/* Why It Matters (V2) */}
                            {kp.whyItMatters && (
                              <div className="text-xs text-slate-500 dark:text-neutral-400 mt-1">
                                → {kp.whyItMatters}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  {analysis.executiveSummary.primaryPurpose && (
                    <div className="mt-3 text-xs text-slate-500 dark:text-neutral-400">
                      <strong>Purpose:</strong> {analysis.executiveSummary.primaryPurpose}
                    </div>
                  )}
                </div>

                {/* Strategic Questions (V2 - New) */}
                {Array.isArray(analysis.strategicQuestions) &&
                  analysis.strategicQuestions.length > 0 && (
                    <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Info className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                          Strategic Questions
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {analysis.strategicQuestions.slice(0, 5).map((q, idx) => (
                          <li
                            key={idx}
                            className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2"
                          >
                            <span className="text-blue-500 dark:text-blue-400 font-bold">?</span>
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Risks & Flags (UPGRADED with cause-consequence) */}
                {Array.isArray(analysis.risks) && analysis.risks.length > 0 && (
                  <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-semibold text-red-900 dark:text-red-200">
                        Risks & Flags
                      </span>
                    </div>
                    <ul className="space-y-3">
                      {analysis.risks.slice(0, 5).map((risk, idx) => (
                        <li
                          key={idx}
                          className="text-sm border-l-2 border-red-300 dark:border-red-700 pl-3"
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                                risk.severity === 'critical'
                                  ? 'bg-red-600 text-white'
                                  : risk.severity === 'high'
                                    ? 'bg-orange-500 text-white'
                                    : risk.severity === 'medium'
                                      ? 'bg-yellow-500 text-black'
                                      : 'bg-slate-300 text-black'
                              }`}
                            >
                              {risk.severity}
                            </span>
                            <span className="text-red-800 dark:text-red-200 font-medium">
                              {risk.risk}
                            </span>
                          </div>
                          {/* Cause-Consequence Chain (V2) */}
                          {(risk.cause || risk.consequence) && (
                            <div className="mt-1 text-xs text-red-700 dark:text-red-300 space-y-1">
                              {risk.cause && (
                                <div>
                                  <strong>Cause:</strong> {risk.cause}
                                </div>
                              )}
                              {risk.consequence && (
                                <div>
                                  <strong>If unchecked:</strong> {risk.consequence}
                                </div>
                              )}
                              {risk.mitigation && (
                                <div className="text-green-700 dark:text-green-300">
                                  <strong>Mitigation:</strong> {risk.mitigation}
                                </div>
                              )}
                            </div>
                          )}
                          {risk.explanation && !risk.cause && (
                            <div className="mt-1 text-xs text-red-700 dark:text-red-300">
                              {risk.explanation}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Missing Elements (V2 - New) */}
                {analysis.materialMatters?.missingElements?.length > 0 && (
                  <div className="rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                        Missing / Not Addressed
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {analysis.materialMatters.missingElements.slice(0, 5).map((item, idx) => (
                        <li key={idx} className="text-sm text-orange-800 dark:text-orange-200">
                          <strong>{item.gap}</strong>
                          {item.risk && (
                            <div className="text-xs text-orange-600 dark:text-orange-300 mt-0.5">
                              Risk: {item.risk}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Material Matters */}
                {analysis.materialMatters?.criticalItems?.length > 0 && (
                  <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Info className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        Material Matters
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {analysis.materialMatters.criticalItems.slice(0, 5).map((item, idx) => (
                        <li
                          key={idx}
                          className="text-sm text-amber-800 dark:text-amber-200 border-l-2 border-amber-300 dark:border-amber-700 pl-3"
                        >
                          <strong>{item.item}</strong>
                          {item.why && (
                            <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                              {item.why}
                            </div>
                          )}
                          {item.consequence && (
                            <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                              → {item.consequence}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key Entities (UPGRADED with exposure) */}
                {analysis.materialMatters?.keyEntities?.length > 0 && (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="h-4 w-4 text-slate-600" />
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        Key Entities
                      </span>
                    </div>
                    <div className="space-y-2">
                      {analysis.materialMatters.keyEntities.slice(0, 8).map((entity, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 text-sm p-2 rounded-lg bg-white dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700"
                        >
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0">
                            {entity.type}
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 dark:text-white">
                              {entity.entity}
                            </div>
                            {entity.role && (
                              <div className="text-xs text-slate-500 dark:text-neutral-400">
                                {entity.role}
                              </div>
                            )}
                            {entity.exposure && (
                              <div className="text-xs text-amber-600 dark:text-amber-400">
                                Exposure: {entity.exposure}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contradictions (if any) */}
                {Array.isArray(analysis.contradictions) && analysis.contradictions.length > 0 && (
                  <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
                        Internal Contradictions
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {analysis.contradictions.slice(0, 3).map((c, idx) => (
                        <li key={idx} className="text-sm text-purple-800 dark:text-purple-200">
                          <div className="font-medium">"{c.statement1?.text}"</div>
                          <div className="text-xs mt-1">vs</div>
                          <div className="font-medium">"{c.statement2?.text}"</div>
                          {c.explanation && (
                            <div className="mt-1 text-xs text-purple-600 dark:text-purple-300">
                              {c.explanation}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Confidence & Coverage (v3.0 - HARDENED) */}
                {analysis.confidenceCoverage && (
                  <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-4 w-4 text-indigo-600" />
                      <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
                        Confidence & Coverage
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      {/* Semantic Confidence */}
                      <div className="text-sm">
                        <div className="font-medium text-indigo-800 dark:text-indigo-200">
                          Semantic Analysis
                        </div>
                        <div
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                            analysis.confidenceCoverage.semanticConfidence === 'high'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : analysis.confidenceCoverage.semanticConfidence === 'medium'
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          }`}
                        >
                          {analysis.confidenceCoverage.semanticConfidence} confidence
                        </div>
                      </div>

                      {/* Deterministic Coverage */}
                      {analysis.confidenceCoverage.deterministicCoveragePercent !== undefined &&
                        analysis.confidenceCoverage.deterministicStatus !== 'not_applicable' && (
                          <div className="text-sm">
                            <div className="font-medium text-indigo-800 dark:text-indigo-200">
                              Numeric Validation
                            </div>
                            <div
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                                analysis.confidenceCoverage.deterministicStatus === 'passed'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                  : analysis.confidenceCoverage.deterministicStatus === 'failed'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                              }`}
                            >
                              {analysis.confidenceCoverage.deterministicCoveragePercent}% coverage
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Overall Assessment */}
                    <div className="text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg p-2">
                      {analysis.confidenceCoverage.overallAssessment}
                    </div>

                    {/* Explicit Gaps */}
                    {analysis.confidenceCoverage.explicitGaps?.length > 0 && (
                      <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">
                        <strong>Gaps:</strong> {analysis.confidenceCoverage.explicitGaps.join('; ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Deterministic Validation Details */}
                {analysis.deterministicValidation &&
                  analysis.deterministicValidation.status !== 'not_applicable' && (
                    <div className="rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Info className="h-4 w-4 text-cyan-600" />
                          <span className="text-sm font-semibold text-cyan-900 dark:text-cyan-200">
                            Numeric Validation
                          </span>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            analysis.deterministicValidation.status === 'passed'
                              ? 'bg-green-600 text-white'
                              : analysis.deterministicValidation.status === 'failed'
                                ? 'bg-red-600 text-white'
                                : 'bg-yellow-500 text-black'
                          }`}
                        >
                          {analysis.deterministicValidation.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
                        <div className="bg-white dark:bg-cyan-900/30 rounded p-2">
                          <div className="font-bold text-lg text-cyan-800 dark:text-cyan-200">
                            {analysis.deterministicValidation.checksRun}
                          </div>
                          <div className="text-cyan-600 dark:text-cyan-400">checks run</div>
                        </div>
                        <div className="bg-white dark:bg-cyan-900/30 rounded p-2">
                          <div className="font-bold text-lg text-green-600">
                            {analysis.deterministicValidation.checksPassed}
                          </div>
                          <div className="text-cyan-600 dark:text-cyan-400">passed</div>
                        </div>
                        <div className="bg-white dark:bg-cyan-900/30 rounded p-2">
                          <div className="font-bold text-lg text-red-600">
                            {analysis.deterministicValidation.checksFailed}
                          </div>
                          <div className="text-cyan-600 dark:text-cyan-400">failed</div>
                        </div>
                      </div>

                      {analysis.deterministicValidation.summaryForAI && (
                        <div className="text-xs text-cyan-700 dark:text-cyan-300 mt-2">
                          {analysis.deterministicValidation.summaryForAI}
                        </div>
                      )}
                    </div>
                  )}

                {/* Calls to Action */}
                {analysis.callsToAction?.length > 0 && (
                  <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Layers className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                        What's Next?
                      </span>
                    </div>
                    <div className="space-y-2">
                      {analysis.callsToAction
                        .filter(c => c.priority === 'critical' || c.priority === 'high')
                        .slice(0, 3)
                        .map((cta, idx) => (
                          <button
                            key={idx}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              cta.priority === 'critical'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-900/50'
                                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-900/50'
                            }`}
                            onClick={() => {
                              // Could integrate with chat or trigger actions
                              console.log('CTA clicked:', cta);
                            }}
                          >
                            <span className="font-medium">{cta.text}</span>
                            <span
                              className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                cta.priority === 'critical'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-emerald-600 text-white'
                              }`}
                            >
                              {cta.priority}
                            </span>
                          </button>
                        ))}
                      {analysis.callsToAction.filter(
                        c => c.priority === 'medium' || c.priority === 'low'
                      ).length > 0 && (
                        <div className="text-xs text-emerald-600 dark:text-emerald-400 pt-1">
                          +
                          {
                            analysis.callsToAction.filter(
                              c => c.priority === 'medium' || c.priority === 'low'
                            ).length
                          }{' '}
                          more suggestions available
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Deterministic Expansion Ladder (NEW - User-Driven Validation) */}
                {analysis.expansionLadder && analysis.expansionLadder.currentPercent < 100 && (
                  <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg
                          className="h-4 w-4 text-violet-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-violet-900 dark:text-violet-200">
                          Increase Validation Coverage
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                          {analysis.expansionLadder.currentPercent}%
                        </span>
                        <span className="text-xs text-violet-500">→</span>
                        <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
                          {analysis.expansionLadder.potentialMaxPercent || 100}%
                        </span>
                      </div>
                    </div>

                    {/* Current Status */}
                    <div className="mb-3 p-2 bg-violet-100 dark:bg-violet-900/40 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-violet-700 dark:text-violet-300">
                          Semantic Analysis
                        </span>
                        <span className="text-xs font-medium text-green-600">100% ✓</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-violet-700 dark:text-violet-300">
                          Deterministic Validation
                        </span>
                        <span className="text-xs font-medium text-amber-600">
                          {analysis.expansionLadder.currentPercent}%
                        </span>
                      </div>
                    </div>

                    {/* Why Not 100% */}
                    {analysis.expansionLadder.blockedBy?.length > 0 && (
                      <div className="mb-3 text-xs text-violet-600 dark:text-violet-400">
                        <strong>Why not 100%:</strong>{' '}
                        {analysis.expansionLadder.blockedBy
                          .map(b => b.replace(/_/g, ' '))
                          .join(', ')}
                      </div>
                    )}

                    {/* Expansion Steps */}
                    {analysis.expansionLadder.expansionSteps?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-violet-800 dark:text-violet-200 mb-2">
                          Upload to increase coverage:
                        </div>
                        {analysis.expansionLadder.expansionSteps
                          .filter(s => s.priority !== 'none')
                          .slice(0, 3)
                          .map((step, idx) => (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg border ${
                                step.priority === 'high'
                                  ? 'border-violet-300 dark:border-violet-700 bg-white dark:bg-violet-900/30'
                                  : 'border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/20'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-violet-600 dark:text-violet-400">
                                      Step {step.step}
                                    </span>
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        step.priority === 'high'
                                          ? 'bg-violet-600 text-white'
                                          : step.priority === 'medium'
                                            ? 'bg-violet-400 text-white'
                                            : 'bg-violet-200 text-violet-700'
                                      }`}
                                    >
                                      {step.priority}
                                    </span>
                                    <span className="text-xs font-medium text-green-600">
                                      {step.expectedCoverageIncrease}
                                    </span>
                                  </div>
                                  <div className="text-sm font-medium text-violet-900 dark:text-violet-100">
                                    {step.description}
                                  </div>
                                  {step.whyNeeded && (
                                    <div className="text-xs text-violet-600 dark:text-violet-400 mt-1">
                                      {step.whyNeeded}
                                    </div>
                                  )}
                                  {step.requiredEvidenceSpec && (
                                    <div className="mt-2 text-[10px] text-violet-500 dark:text-violet-500">
                                      <strong>Requirements:</strong>{' '}
                                      {Object.entries(step.requiredEvidenceSpec)
                                        .map(([k, v]) => `${k}: ${v}`)
                                        .join(' • ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        {analysis.expansionLadder.expansionSteps.filter(s => s.priority !== 'none')
                          .length > 3 && (
                          <div className="text-xs text-violet-600 dark:text-violet-400">
                            +
                            {analysis.expansionLadder.expansionSteps.filter(
                              s => s.priority !== 'none'
                            ).length - 3}{' '}
                            more steps available
                          </div>
                        )}
                      </div>
                    )}

                    {/* Important Note */}
                    <div className="mt-3 text-[10px] text-violet-500 dark:text-violet-500 border-t border-violet-200 dark:border-violet-700 pt-2">
                      <strong>Note:</strong> Semantic analysis always covers 100% of content.
                      Deterministic validation verifies numeric correctness using code — not AI.
                      Uploading evidence is optional but increases validation certainty.
                    </div>
                  </div>
                )}

                {/* PLATINUM-TIER: Visual Context (PowerPoint, Diagrams) */}
                {analysis.visualContext && analysis.visualContext.visualElements?.length > 0 && (
                  <div className="rounded-xl border border-pink-200 dark:border-pink-800 bg-pink-50 dark:bg-pink-900/20 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        className="h-4 w-4 text-pink-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-pink-900 dark:text-pink-200">
                        Visual Context Analysis
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-pink-600 text-white">
                        {analysis.visualContext.visualElements.length} visual elements
                      </span>
                    </div>

                    <div className="space-y-2">
                      {analysis.visualContext.visualElements.slice(0, 4).map((ve, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 text-sm text-pink-800 dark:text-pink-200"
                        >
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              ve.type === 'chart'
                                ? 'bg-blue-500 text-white'
                                : ve.type === 'diagram'
                                  ? 'bg-purple-500 text-white'
                                  : ve.type === 'image'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-pink-500 text-white'
                            }`}
                          >
                            {ve.type}
                          </span>
                          <span className="text-xs text-pink-600 dark:text-pink-400">
                            {ve.location}
                          </span>
                          <span className="flex-1 text-xs">{ve.interpretation}</span>
                        </div>
                      ))}
                      {analysis.visualContext.visualElements.length > 4 && (
                        <div className="text-xs text-pink-600 dark:text-pink-400">
                          +{analysis.visualContext.visualElements.length - 4} more visual elements
                        </div>
                      )}
                    </div>

                    {analysis.visualContext.requiresVisionModel && (
                      <div className="mt-2 text-[10px] text-pink-500 border-t border-pink-200 dark:border-pink-700 pt-2">
                        <strong>Note:</strong> Some visual elements require vision model analysis
                        for full interpretation.
                      </div>
                    )}
                  </div>
                )}

                {/* PLATINUM-TIER: Structural Integrity (Word Documents) */}
                {analysis.structuralIntegrity && (
                  <div
                    className={`rounded-xl border p-4 ${
                      analysis.structuralIntegrity.isValid
                        ? 'border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20'
                        : 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg
                          className={`h-4 w-4 ${analysis.structuralIntegrity.isValid ? 'text-teal-600' : 'text-orange-600'}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                        <span
                          className={`text-sm font-semibold ${analysis.structuralIntegrity.isValid ? 'text-teal-900 dark:text-teal-200' : 'text-orange-900 dark:text-orange-200'}`}
                        >
                          Document Structure
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            analysis.structuralIntegrity.isValid
                              ? 'bg-teal-600 text-white'
                              : 'bg-orange-600 text-white'
                          }`}
                        >
                          {analysis.structuralIntegrity.isValid
                            ? 'Valid'
                            : `${analysis.structuralIntegrity.issueCount} issues`}
                        </span>
                        {analysis.structuralIntegrity.sectionCount > 0 && (
                          <span className="text-xs text-slate-500">
                            {analysis.structuralIntegrity.sectionCount} sections
                          </span>
                        )}
                      </div>
                    </div>

                    {analysis.structuralIntegrity.outline?.length > 0 && (
                      <div className="mb-3 text-xs space-y-1">
                        <div className="font-medium text-slate-700 dark:text-slate-300">
                          Document Outline:
                        </div>
                        {analysis.structuralIntegrity.outline.slice(0, 5).map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-1 text-slate-600 dark:text-slate-400"
                            style={{ paddingLeft: `${(item.level || 0) * 12}px` }}
                          >
                            <span className="text-[10px] text-slate-400">
                              {'→'.repeat(item.level || 0)}
                            </span>
                            <span className="truncate">{item.title}</span>
                          </div>
                        ))}
                        {analysis.structuralIntegrity.outline.length > 5 && (
                          <div className="text-slate-500">
                            +{analysis.structuralIntegrity.outline.length - 5} more sections
                          </div>
                        )}
                      </div>
                    )}

                    {analysis.structuralIntegrity.issues?.length > 0 && (
                      <div className="space-y-1">
                        {analysis.structuralIntegrity.issues.slice(0, 3).map((issue, idx) => (
                          <div
                            key={idx}
                            className={`text-xs p-2 rounded ${
                              issue.severity === 'error'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                : issue.severity === 'warning'
                                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200'
                                  : 'bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            <span className="font-medium">{issue.type?.replace(/_/g, ' ')}:</span>{' '}
                            {issue.description}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* PLATINUM-TIER: Entity Graph (Text Files) */}
                {analysis.entityGraph && analysis.entityGraph.entityCount > 0 && (
                  <div className="rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <svg
                          className="h-4 w-4 text-cyan-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-cyan-900 dark:text-cyan-200">
                          Entity Extraction
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-600 text-white">
                          {analysis.entityGraph.entityCount} entities
                        </span>
                        {analysis.entityGraph.claimCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-500 text-white">
                            {analysis.entityGraph.claimCount} claims
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Entity Summary */}
                    {analysis.entityGraph.entitySummary &&
                      Object.keys(analysis.entityGraph.entitySummary).length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {Object.entries(analysis.entityGraph.entitySummary).map(
                            ([type, count]) => (
                              <span
                                key={type}
                                className="px-2 py-1 rounded-lg text-[10px] font-medium bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200"
                              >
                                {type}: {count}
                              </span>
                            )
                          )}
                        </div>
                      )}

                    {/* Cross-file Links */}
                    {analysis.entityGraph.crossFileLinkCount > 0 && (
                      <div className="text-xs text-cyan-700 dark:text-cyan-300 p-2 bg-cyan-100 dark:bg-cyan-900/40 rounded-lg">
                        <strong>
                          {analysis.entityGraph.crossFileLinkCount} potential cross-file links
                        </strong>{' '}
                        detected. Upload related documents to verify connections.
                      </div>
                    )}
                  </div>
                )}

                {/* Content Classification Badge */}
                {analysis.contentClassification && (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">
                      Classification:
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                      {analysis.contentClassification.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">•</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                      {analysis.analysisLane}
                    </span>
                  </div>
                )}

                {/* Legacy Confidence Notes (fallback) */}
                {!analysis.confidenceCoverage && analysis.confidenceNotes && (
                  <div className="text-xs text-slate-500 dark:text-neutral-400 border-t border-slate-200 dark:border-slate-700 pt-3">
                    <strong>Confidence:</strong> {analysis.confidenceNotes.overallConfidence}
                    {analysis.confidenceNotes.caveats?.length > 0 && (
                      <span className="ml-2">• {analysis.confidenceNotes.caveats.join(' • ')}</span>
                    )}
                  </div>
                )}
              </div>
            );
          }

          // ✅ FALLBACK 1: Legacy autoBrief string
          if (brief) {
            return (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    Document Briefing
                  </span>
                </div>
                <MarkdownText content={brief} />
              </div>
            );
          }

          // ✅ FALLBACK 2: autoBriefFindings array
          const bestFinding = pickBestFinding(file);
          if (bestFinding?.text) {
            return (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    Key Insight
                  </span>
                  {bestFinding.certainty && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${
                        bestFinding.certainty === 'high'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : bestFinding.certainty === 'medium'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                      }`}
                    >
                      {bestFinding.certainty} confidence
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200">{bestFinding.text}</div>
                {bestFinding.cite && (
                  <div className="mt-2 text-xs text-slate-500 dark:text-neutral-400">
                    Source: {bestFinding.cite}
                  </div>
                )}
              </div>
            );
          }

          // ✅ FALLBACK 3: nanoMetadata micro_insight
          const nanoMicroRaw =
            file?.nanoMetadata?.details?.micro_insight || file?.nanoMetadata?.details?.microInsight;
          const isReadyState = trustState === 'trusted' || trustState === 'readable';
          const fileIdForAnalysis = file?.fileId || file?.id || file?._id;
          const hasFileAnalysis = Boolean(
            fileIdForAnalysis && analysisData?.fileAnalyses?.[String(fileIdForAnalysis)]
          );
          const analysisFailure = fileIdForAnalysis
            ? fileAnalysisFailures[String(fileIdForAnalysis)]
            : null;
          const canGenerateSummary = isReadyState && fileIdForAnalysis && !hasFileAnalysis;
          const nanoMicro = sanitizeMicroInsight(nanoMicroRaw, { isReady: isReadyState });
          const pageCount =
            file?.pageCount ||
            file?.nanoMetadata?.pageCount ||
            file?.nanoMetadata?.details?.page_count ||
            0;
          const fallbackSnapshot =
            isReadyState && !nanoMicro
              ? pageCount
                ? `Scanned PDF (${pageCount} page${pageCount === 1 ? '' : 's'}).`
                : 'Document is ready.'
              : '';
          const snapshotText = nanoMicro || fallbackSnapshot;
          if (snapshotText) {
            return (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
                {analysisFailure && (
                  <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-700 dark:text-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                      <div>
                        <div className="font-semibold">Analysis paused</div>
                        <div className="mt-1">
                          {analysisFailure.lastError ||
                            'This file could not be analyzed automatically.'}
                        </div>
                        <div className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-200/80">
                          Attempts: {analysisFailure.attempts || 1} / {FILE_ANALYSIS_MAX_RETRIES}
                        </div>
                        {analysisFailure.canRetry !== false && (
                          <button
                            type="button"
                            onClick={() => runFileAnalysisNow(String(fileIdForAnalysis), true)}
                            disabled={analysisRunning}
                            className="mt-2 text-[11px] px-2 py-1 rounded-md border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-200 hover:bg-amber-100/60 dark:hover:bg-amber-800/40 disabled:opacity-60"
                          >
                            {analysisRunning ? 'Retrying…' : 'Retry now'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-semibold text-slate-900 dark:text-white">
                    Document snapshot
                  </span>
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200">{snapshotText}</div>
                {isReadyState && (
                  <div className="mt-2 text-xs text-slate-500 dark:text-neutral-400">
                    Full summary is being prepared in the background.
                  </div>
                )}
                {canGenerateSummary && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => runFileAnalysisNow(String(fileIdForAnalysis), true)}
                      disabled={analysisRunning}
                      className="text-xs px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-neutral-200 hover:bg-slate-50 dark:hover:bg-neutral-800 disabled:opacity-60"
                    >
                      {analysisRunning ? 'Generating summary…' : 'Generate summary now'}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          // ✅ TRUST UX: Status-based empty state (NO action buttons)
          // Analysis will auto-run - users should feel confident it's happening
          if (trustState === 'trusted' || trustState === 'readable') {
            return (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Clock className="h-5 w-5 text-slate-400 dark:text-slate-500 animate-pulse" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Generating summary…
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      We’re preparing the detailed insights now. Check back in a moment.
                    </div>
                    {canGenerateSummary && (
                      <button
                        type="button"
                        onClick={() => runFileAnalysisNow(String(fileIdForAnalysis), true)}
                        disabled={analysisRunning}
                        className="mt-2 text-xs px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-neutral-200 hover:bg-slate-50 dark:hover:bg-neutral-800 disabled:opacity-60"
                      >
                        {analysisRunning ? 'Generating summary…' : 'Generate summary now'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })()}
      </div>
    );
  };

  if (!notebook) return null;

  return (
    <div
      id={anchorId}
      className="mx-4 my-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 overflow-hidden"
    >
      {/* Header with Confidence Status (NO action buttons - deterministic trust) */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white">
          <FileText className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            {headerTitle}
          </div>
          <div className="text-xs text-slate-500 dark:text-neutral-400">
            {headerSubtitle}
          </div>
          {vaultNarrativeText &&
            (notebookTrustState === 'trusted' || notebookTrustState === 'readable') && (
              <div
                className="mt-1 text-[11px] text-slate-600 dark:text-neutral-300 truncate"
                title={vaultNarrativeText}
              >
                Vault identity: {vaultNarrativeText}
              </div>
            )}
        </div>
        {/* ✅ TRUST UX: Confidence Status Badge (replaces action buttons) */}
        {(() => {
          const readiness = getNotebookReadiness(files, analysisData);
          if (readiness.ready) {
            const badge = getConfidenceBadge(readiness.level);
            const BadgeIcon = badge.icon;
            return (
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border ${badge.className}`}
                title={badge.tooltip}
              >
                <BadgeIcon className="h-3.5 w-3.5" />
                <span>{readiness.message}</span>
              </div>
            );
          }
          if (readiness.reason === 'processing') {
            return (
              <div className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700">
                <Clock className="h-3.5 w-3.5 animate-pulse" />
                <span>{readiness.message}</span>
              </div>
            );
          }
          if (readiness.reason === 'low_quality') {
            return (
              <div className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800/60">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{readiness.message}</span>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* ✅ TRUST UX: "Notebook Ready" Celebration Moment (one-time, subtle) */}
      {showReadyCelebration && (
        <div className="mx-4 mt-3 mb-0 animate-in fade-in slide-in-from-top-2 duration-500">
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                  ✨ This notebook is ready
                </div>
                <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                  {files.length === 1
                    ? 'The document has been analyzed.'
                    : `All ${files.length} documents have been analyzed.`}{' '}
                  You can rely on the insights shown below.
                </div>
              </div>
              <button
                onClick={() => setShowReadyCelebration(false)}
                className="flex-shrink-0 p-1 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-800/30 text-emerald-600 dark:text-emerald-400 transition-colors"
                title="Dismiss"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/30 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {tabItems.map(t => {
            const isActive = String(activeTab) === String(t.id);
            const isJustUnlocked = justUnlockedTabIds.includes(String(t.id));
            const hasNewAnalysis = tabsWithNewAnalysis.has(String(t.id));

            // ✅ ENTERPRISE: Clear ALL highlights (ring, pulse, glow) when tab is clicked
            const handleTabClick = () => {
              setActiveTab(String(t.id));
              // Clear analysis highlight
              if (hasNewAnalysis) {
                setTabsWithNewAnalysis(prev => {
                  const next = new Set(prev);
                  next.delete(String(t.id));
                  return next;
                });
              }
              // Clear "just unlocked" highlight
              if (isJustUnlocked) {
                setJustUnlockedTabIds(prev => prev.filter(id => id !== String(t.id)));
              }
            };

            // ✅ ENTERPRISE: No ring on active tab or after clicking
            const showRing = !isActive && (isJustUnlocked || hasNewAnalysis);
            const ringClass = showRing
              ? hasNewAnalysis
                ? 'ring-2 ring-green-400 dark:ring-green-600 bg-green-50 dark:bg-green-900/20'
                : 'ring-2 ring-amber-300 dark:ring-amber-700 animate-pulse'
              : '';

            return (
              <button
                key={t.id}
                onClick={handleTabClick}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 min-w-[100px] max-w-[160px] ${
                  isActive
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800/40'
                    : 'text-slate-600 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 border border-transparent'
                } ${ringClass}`}
                title={t.label}
              >
                {'fileName' in t ? <TabIcon fileName={t.fileName} /> : t.icon}
                <span className="truncate flex-1 text-left" dir="auto">
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-h-[360px] overflow-y-auto">
        {activeTab === 'all' ? renderAll() : renderFile(activeFile)}
      </div>
    </div>
  );
}
